import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  User,
  ChevronDown,
  LayoutDashboard,
  UserCircle,
  Package,
  LogOut,
  LogIn,
  UserPlus,
  Shield,
  Settings,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../../../context/AuthContext";
import { env } from "../../../../../config/env";

const PublicProfileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { user, logout } = useAuth();

  const getProfilePhotoUrl = (
    path: string | null | undefined
  ): string | undefined => {
    if (!path) return undefined;

    if (path.startsWith("http")) {
      return path;
    }

    return `${env.IMAGE_BASE_URL}${path.replace(/^\//, "")}`;
  };

  // Vérifie si l'utilisateur peut accéder à la gestion du site
  const isAdmin = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles)) {
      return false;
    }

    return user.roles.some(
      (role) =>
        role.name === "admin" ||
        role.name === "platform_admin"
    );
  }, [user]);

  // Fermer le menu lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Déconnexion
  const handleLogout = async () => {
    setIsOpen(false);

    await logout();

    navigate("/auth/login");
  };

  // Menu utilisateur
  const userItems = [
    {
      icon: <LayoutDashboard size={16} />,
      label: "Mon espace",
      description: "Voir mon activité",
      path: "/user/dashboard/home",
    },
    {
      icon: <UserCircle size={16} />,
      label: "Mon profil",
      description: "Gérer mes informations",
      path: "/user/dashboard/profile",
    },
    {
      icon: <Package size={16} />,
      label: "Mes commandes",
      description: "Voir mes achats",
      path: "/user/dashboard/orders",
    },
  ];

  // Menu gestion du site
  const adminItems = [
    {
      icon: <LayoutDashboard size={16} />,
      label: "Tableau de bord",
      description: "Voir l'activité et les statistiques",
      path: "/admin/dashboard/home",
    },
    {
      icon: <Shield size={16} />,
      label: "Sécurité",
      description: "Gérer la sécurité et les accès",
      path: "/admin/dashboard/security",
    },
    {
      icon: <Settings size={16} />,
      label: "Réglages",
      description: "Configurer le fonctionnement du site",
      path: "/admin/dashboard/settings",
    },
  ];

  // Menu visiteur
  const guestItems = [
    {
      icon: <LogIn size={16} />,
      label: "Se connecter",
      description: "Accéder à mon compte",
      path: "/auth/login",
    },
    {
      icon: <UserPlus size={16} />,
      label: "Créer un compte",
      description: "Créer mon espace personnel",
      path: "/auth/register",
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="bg-white/30 text-white hover:bg-white/50 font-semibold p-2.5 rounded-full shadow-md hover:scale-105 transition-transform duration-200 flex items-center gap-2"
        aria-label="Menu utilisateur"
        aria-expanded={isOpen}
      >
        <User size={20} />

        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl overflow-hidden z-50"
          >
            {/* Informations utilisateur */}
            {user && (
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                    {user.profile_photo_path ? (
                      <img
                        src={getProfilePhotoUrl(user.profile_photo_path)}
                        alt={`${user.first_name} ${user.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-500">
                        <User size={18} />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {user.first_name} {user.last_name}
                    </p>

                    <p className="text-xs text-gray-500 truncate">
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Espace personnel */}
            {user && (
              <div className="py-2">
                <div className="px-4 pb-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Mon espace
                  </p>
                </div>

                {userItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="flex items-start gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="mt-0.5 text-gray-500">
                      {item.icon}
                    </div>

                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {item.label}
                      </p>

                      <p className="text-xs text-gray-400 mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Gestion du site */}
            {user && isAdmin && (
              <>
                <div className="border-t border-gray-100" />

                <div className="py-2">
                  <div className="px-4 pb-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Gestion du site
                    </p>
                  </div>

                  {adminItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className="flex items-start gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <div className="mt-0.5 text-gray-500">
                        {item.icon}
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {item.label}
                        </p>

                        <p className="text-xs text-gray-400 mt-0.5">
                          {item.description}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}

            {/* Visiteur */}
            {!user && (
              <div className="py-2">
                {guestItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="flex items-start gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <div className="mt-0.5 text-gray-500">
                      {item.icon}
                    </div>

                    <div>
                      <p className="text-sm font-medium">
                        {item.label}
                      </p>

                      <p className="text-xs text-gray-400 mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Déconnexion */}
            {user && (
              <>
                <div className="border-t border-gray-100" />

                <div className="py-1">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />

                    <span>Déconnexion</span>
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PublicProfileMenu;